import { useState } from "react";
import { Printer } from "lucide-react";
import { Card } from "../../ui/card";
import { ErrorCard, LoadingBlock, Pill } from "../AdminBits";
import { AdminTable, FilterTabs, ListFrame, MoreFooter } from "../ListBits";
import useAdminFetch from "../../../hooks/useAdminFetch";
import useAdminList from "../../../hooks/useAdminList";
import { JOB_MODE_LABELS, JOB_STATUS } from "../../../lib/jamoraAdmin";
import { formatDateTime } from "../../../lib/tickets";

const STATUS_TABS = [["", "Tous"], ["done", "Terminés"], ["failed", "Échecs"], ["pending", "En attente"], ["processing", "En cours"]];

// The workstations and print jobs of a pharmacy. A job shows where it was printed and why it failed, never the content of the ticket.
export default function PrintingTab({ schema }) {
  const agents = useAdminFetch(`/pharmacies/${schema}/print-agents`);
  const [status, setStatus] = useState("");
  const jobs = useAdminList(`/pharmacies/${schema}/print-jobs`, { status });
  const agentColumns = [
    { id: "label", label: "Poste", primary: true, cell: (a) => <span>{a.label || "—"}{a.hostname && <span className="block text-xs text-slate-500">{a.hostname}</span>}</span> },
    { id: "state", label: "État", cell: (a) => (a.active ? <Pill tone={a.online ? "emerald" : "slate"}>{a.online ? "En ligne" : "Hors ligne"}</Pill> : <Pill tone="red">Révoqué</Pill>) },
    { id: "version", label: "Version", hide: "lg", cell: (a) => a.version || "—" },
    { id: "seen", label: "Dernier signal", cell: (a) => (a.last_seen_at ? formatDateTime(a.last_seen_at) : "—") },
    { id: "printers", label: "Imprimantes", hide: "lg", cell: (a) => (a.printers.length ? a.printers.join(", ") : "—") },
  ];
  const jobColumns = [
    { id: "printer", label: "Imprimante", primary: true, cell: (j) => j.printer || "—" },
    { id: "agent", label: "Poste", cell: (j) => j.agent_label || "—" },
    { id: "status", label: "Statut", cell: (j) => { const [text, tone] = JOB_STATUS[j.status] || [j.status, "slate"]; return <Pill tone={tone}>{text}</Pill>; } },
    { id: "failure", label: "Code d'échec", hide: "lg", cell: (j) => j.failure_code || "—" },
    { id: "mode", label: "Mode", hide: "lg", cell: (j) => JOB_MODE_LABELS[j.mode] || j.mode },
    { id: "source", label: "Origine", hide: "xl", cell: (j) => j.source_type || "—" },
    { id: "date", label: "Date", cell: (j) => formatDateTime(j.created_at) },
  ];
  return (
    <div className="space-y-6" data-testid="pharmacy-printing">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Postes d'impression</h2>
        {agents.phase === "loading" || agents.phase === "idle" ? <LoadingBlock rows={2} />
          : agents.phase === "error" ? <ErrorCard message={agents.error} onRetry={agents.reload} testid="agents-error" />
            : !agents.data.items.length ? <Card className="p-4 text-sm text-slate-500" data-testid="agents-empty">Aucun poste n'est pairé dans cette pharmacie.</Card>
              : <AdminTable columns={agentColumns} rows={agents.data.items} rowKey={(a) => `${a.label}-${a.hostname}-${a.paired_at}`} testid="agents-table" />}
      </section>
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Travaux d'impression</h2>
        <FilterTabs label="Statut du travail" tabs={STATUS_TABS} value={status} onChange={setStatus} testid="jobs-status" />
        <ListFrame list={jobs} rows={3} empty={{ icon: Printer, title: "Aucun travail", hint: status ? "Essayez un autre statut." : "Aucune impression n'a encore été demandée.", testid: "jobs-empty" }}>
          <AdminTable columns={jobColumns} rows={jobs.items} rowKey={(j) => j.id} testid="jobs-table" />
          <MoreFooter list={jobs} noun="travail(aux)" testid="jobs" />
        </ListFrame>
      </section>
    </div>
  );
}
